import { createContext, useContext, useState, useCallback, useRef } from 'react';

const StickyMobileBarContext = createContext(null);

// Shallow comparison helper (ignores function properties since they're wrappers)
function shallowEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return obj1 === obj2;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    // Skip function properties - they're stable wrappers
    if (typeof obj1[key] === 'function' && typeof obj2[key] === 'function') {
      continue;
    }
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
}

export function StickyMobileBarProvider({ children }) {
  const [barData, setBarData] = useState(null);
  const prevDataRef = useRef(null);

  const setStickyBarData = useCallback((data) => {
    // Only update if data actually changed (shallow comparison)
    if (!shallowEqual(prevDataRef.current, data)) {
      prevDataRef.current = data;
      setBarData(data);
    }
  }, []);

  const clearStickyBarData = useCallback(() => {
    if (prevDataRef.current !== null) {
      prevDataRef.current = null;
      setBarData(null);
    }
  }, []);

  return (
    <StickyMobileBarContext.Provider
      value={{
        barData,
        setStickyBarData,
        clearStickyBarData,
      }}
    >
      {children}
    </StickyMobileBarContext.Provider>
  );
}

export function useStickyMobileBar() {
  const context = useContext(StickyMobileBarContext);
  if (!context) {
    throw new Error('useStickyMobileBar must be used within StickyMobileBarProvider');
  }
  return context;
}
